/**
 * @file agencyPricing.js
 * @description Single source for the agency-page pricing copy (plans from Jan 2027). Starter and Pro prices,
 *   included openings, extra-opening prices, the free-period count and end date, and whether ABAcases Workforce
 *   is named are all read from getPlans() / getStarterPromoDates() / platform_config, never typed into strings.
 * @module lib/agencyPricing
 */
import { getPlans, getStarterPromoDates } from "./plans";
import { getCachedConfig } from "./configService";
import { proTeamLogins, isWorkforceNamed } from "./entitlements";

/**
 * @param {string} [language] i18n language code (es* selects the Spanish date format)
 * @returns {{
 *   isPromoActive:boolean, promoCount:number, endDate:string, endDateShort:string, startDate:string,
 *   starterMo:number, starterIncluded:number, starterExtra:number,
 *   proMo:number, proAnnualMo:number, proCases:number, proExtra:number, teamLogins:number,
 *   workforceIncluded:boolean, workforceMaxStarter:number, workforceMaxPro:number,
 *   count:number, price:number
 * }} `count` and `price` are the old names of promoCount and starterExtra, kept until every caller moved.
 */
export function getAgencyPricing(language) {
  const isEs = String(language || "").toLowerCase().startsWith("es");
  const promo = getStarterPromoDates();
  const plans = getPlans();
  const starter = plans.find((p) => p.id === "starter");
  const pro = plans.find((p) => p.id === "pro");
  const promoCount = Number(getCachedConfig("max_free_cases_agency")) || 2;
  const starterPrice = Number(getCachedConfig("extra_case_slot_price_starter")) || 29;
  return {
    isPromoActive: promo.isPromoActive,
    promoCount,
    endDate: isEs ? promo.endDateStrEs : promo.endDateStr,
    // First day of the new plans (the day after the free period ends), e.g. "January 1, 2027".
    endDateShort: isEs ? promo.endDateShortStrEs : promo.endDateShortStr,
    startDate: isEs ? promo.resumeDateStrEs : promo.resumeDateStr,
    // Starter IS the existing extra-opening subscription: the plan price and the extra-opening price are one key.
    starterMo: starterPrice,
    starterIncluded: starter?.included || 1,
    starterExtra: starterPrice,
    proMo: pro?.mo || 179,
    proAnnualMo: pro?.annualMo || 150,
    proCases: pro?.cases || 10,
    proExtra: pro?.extraMo || 15,
    teamLogins: proTeamLogins(getCachedConfig),
    // ABAcases Workforce is named on public pages only once it is released (platform_config workforce_enabled).
    workforceIncluded: isWorkforceNamed(),
    // People each plan can track in ABAcases Workforce (Admin: workforce_max_people_starter / _pro).
    workforceMaxStarter: Number(getCachedConfig("workforce_max_people_starter")) || 15,
    workforceMaxPro: Number(getCachedConfig("workforce_max_people_pro")) || 500,
    count: promoCount,
    price: starterPrice,
  };
}

export { isWorkforceNamed };

/** One rule, owned by the Billing slot planner (lib/billing/slotPlanner): the marketing pages read it from here. */
export { proBreakEvenCases } from "./billing/slotPlanner";
